import { Grid } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import WSDashboardCard from "../../../Components/CardListing/WSDashboardCard";
import WSFilterButtonGroup from "../../../Components/Filter/WSFilterButtonGroup";
import WSButton from "../../../Components/Button/WSButton";
import WSDataGridNew, {
  IColumns,
} from "../../../Components/DataGrid/WSDataGridNew";
import { WSIcons } from "../../../commonFunctions/iIcons";
import { IHeaderRows } from "../../../Components/Filter/WSHeaderFilter";
import {
  callAPI,
  getLocalValue,
  LoacalVariables,
  Method,
  updateMainHeader,
} from "../../../commonFunctions/CommonFunctions";
import { useNavigate, useParams } from "react-router-dom";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import http from "../../../commonFunctions/httpClient";

// ✅ Corrected Interfaces
interface Patient {
  customerProfileId: string;
  name:string;
}

interface Service {
  testId: string;
  testName: string | null;
  rate: number;
  reportUrl: string;
}

interface LabBooking {
  bookingId: string;
  labId: string;
  labName: string;
  testId: string | null;
  testName: string | null;
  userId: string;
  username: string | null;
  patients: Patient[];
  slotId: string;
  slotTime: string;
  collectionType: string;
  address: {
    line1: string;
    line2: string;
    pinCode: string;
  };
  labContact: string | null;
  paymentMode: string;
  paymentStatus: string;
  rate: number;
  collectionFee: number;
  serviceFee: number;
  totalAmount: number;
  status: string;
  services: Service[];
  scheduledDate: string;
}

interface AssignOrder {
  id: string;
  bookingId: string;
  scheduledDate: string;
  patientName: string;
  testName: string;
  collectionType: string;
  paymentStatus: string;
  actions: string;
}

interface LabSummary {
  todaysOrders: number;
  todaysHomeCollections: number;
  totalTests: number;
  totalPackages: number;
}


export const LabOrdersList: React.FC<any> = React.memo((props) => {
  const navigate = useNavigate();
  const filters = ["Test", "Packages"];
  const [selectedFilter, setSelectedFilter] = useState(filters[0]);
  const params = useParams();
  const id = params?.id;

  const [labId, setLabId] = useState<string>("");
  const [orders, setOrders] = useState<AssignOrder[]>([]);
  const [loading, setLoading] = useState(true);

  
  // ✅ Get vendorId from localStorage
  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    setLabId(userId || "");
  }, []);

  useEffect(() => {
    if (!labId) return;

    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response: any = await http.get(`lab/LabBooking/lab/${labId}`);
        const apiData: LabBooking[] = Array.isArray((response as any).data)
          ? (response as any).data
          : [];

        const transformedOrders: AssignOrder[] = apiData.map((booking) => ({
          id: booking.bookingId,
          bookingId: booking.bookingId,
          scheduledDate: booking.scheduledDate,
            patientName: booking.patients?.map((p) => p.name).join(", ") || booking.username || "N/A",
          testName: booking.services?.[0]?.testName || "N/A",
          collectionType: booking.collectionType || "N/A",
          paymentStatus: booking.paymentStatus || "N/A",
          actions: "",
        }));

        setOrders(transformedOrders);
      } catch (error) {
        console.error("Failed to fetch orders:", error);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [labId]);



  useEffect(() => {
    if (!labId) return;

    const fetchSummary = async () => {
      try {
        console.log("📡 FETCHING SUMMARY: lab/LabBooking/summary/", labId);
        const response = await callAPI(
          `lab/LabBooking/summary/${labId}`,
          Method.GET
        );
        
        console.log('✅ SUMMARY RESPONSE:', response);
        
        // ✅ FIXED: Match your API structure {success: true, data: {...}}
        const summaryApiData = response?.data || {};
        console.log('✅ SUMMARY DATA:', summaryApiData);
        
        setSummaryData({
          todaysOrders: summaryApiData.todaysOrders || 0,
          todaysHomeCollections: summaryApiData.todaysHomeCollections || 0,
          totalTests: summaryApiData.totalTests || 0,
          totalPackages: summaryApiData.totalPackages || 0,
        });

        console.log('✅ SUMMARY STATE SET:', {
          todaysOrders: summaryApiData.todaysOrders,
          todaysHomeCollections: summaryApiData.todaysHomeCollections,
          totalTests: summaryApiData.totalTests,
          totalPackages: summaryApiData.totalPackages
        });
      } catch (error) {
        console.error("Failed to fetch lab summary:", error);
      }
    };

    fetchSummary();
  }, [labId]);

  useEffect(() => {
    updateMainHeader("Orders");
  }, []);

  useEffect(() => {
    breadCrumbsStore.updateBreadCrumbs([
      { "Technician Management": "technician-management/list" },
      {
        [!id ? "Assign Order" : ""]: "/",
      },
    ]);
  }, [id]);

  const handleAction = async (action: string, row: any) => {
    if (action === "view") {
      navigate(`/lab-orders/list/view/${row?.id}`);
    }
  };

  // ✅ Updated Columns
  const columns: IColumns[] = [
    { header: "Order ID", fieldName: "bookingId", type: "default" },
    { header: "Date", fieldName: "scheduledDate", type: "default" },
    { header: "Patient Name", fieldName: "patientName", type: "default" },
    { header: "Test / Package", fieldName: "testName", type: "default" },
    { header: "Slot Booking", fieldName: "scheduledDate", type: "default" },

    { header: "Collection Type", fieldName: "collectionType", type: "default" },
    { header: "Status", fieldName: "paymentStatus", type: "status" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["view"],
      actionCallback: (action, row) => handleAction(action, row),
    },
  ];

  const filterRows: IHeaderRows[] = [
    {
      label: "Collection",
      fieldName: "collectionType",
      type: "dropdown",
      options: [
        { label: "Home", value: "Home" },
        { label: "Center", value: "Center" },
      ],
      operator: "eq",
      rowDivision: 3,
    },
    {
      label: "Status",
      fieldName: "paymentStatus",
      type: "dropdown",
      options: [
        // { label: "All", value: "" },
        { label: "Pending", value: "Pending" },
        { label: "Completed", value: "Completed" },
      ],
      operator: "eq",
      rowDivision: 3,
    },
    {
      label: "By Date",
      fieldName: "date",
      type: "date",
      operator: "like",
      rowDivision: 6,
    },
  ];


  const [summaryData, setSummaryData] = useState<LabSummary | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      if (!labId) return;
      
      try {
        setLoading(true);
        const response = await callAPI(
          `lab/LabBooking/summary/${labId}`,
          Method.GET
        );
        
        if (response.status) {
          setSummaryData(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch lab summary:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [labId]);

  if (loading) {
    return (
      <Grid container spacing={2}>
        {/* Show skeleton or spinner */}
        <Grid item sm={3}>
          <div>Loading...</div>
        </Grid>
      </Grid>
    );
  }

   

  return (
    <>
      <Grid container spacing={2}>
      <Grid item sm={3}>
        <WSDashboardCard
          title="Today Orders"
          value={summaryData?.todaysOrders?.toString() || "0"}
          percentage="+2.5%"
          bottomIcon={<WSIcons.TrendingUp />}
          icon={<WSIcons.ShoppingCartIcon sx={{ color: "#098963" }} />}
          onClick={() => console.log("Today Orders clicked")}
        />
      </Grid>
      
      <Grid item sm={3}>
        <WSDashboardCard
          title="Today Home Collections"
          value={summaryData?.todaysHomeCollections?.toString() || "0"}
          percentage="+2.5%"
          bottomIcon={<WSIcons.TrendingUp />}
          icon={<WSIcons.InStockIcon sx={{ color: "#098963" }} />}
          onClick={() => console.log("Home Collections clicked")}
        />
      </Grid>
      
      <Grid item sm={3}>
        <WSDashboardCard
          title="Total Tests"
          value={summaryData?.totalTests?.toString() || "0"}
          percentage="+2.5%"
          bottomIcon={<WSIcons.TrendingUp />}
          icon={<WSIcons.OutOfStockIcon sx={{ color: "#098963" }} />}
          onClick={() => console.log("Total Tests clicked")}
        />
      </Grid>
      
      <Grid item sm={3}>
        <WSDashboardCard
          title="Total Packages"
          value={summaryData?.totalPackages?.toString() || "0"}
          percentage="+2.5%"
          bottomIcon={<WSIcons.TrendingUp />}
          icon={<WSIcons.ShoppingCartIcon sx={{ color: "#098963" }} />}
          onClick={() => console.log("Total Packages clicked")}
        />
      </Grid>
    </Grid>

      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
        <Grid item sm={12}>
          <WSDataGridNew
            columns={columns}
            id="labOrdersGrid"
            title="Lab Orders"
            data={orders}
            exportCsv={true}
            filterFields={filterRows}
            addButton={{ label: "New", color: "secondary" }}
            searchBox={{ fieldName: "bookingId" }}
            gridHeaderSx={{
              backgroundColor: "whitesmoke",
              border: "1px solid whitesmoke",
            }}
          />
        </Grid>
      </Grid>
    </>
  );
});
